import React, { useState, useCallback } from 'react';
import {
  Upload,
  Factory,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  X,
  TrendingDown,
  Package,
  Store,
  ShieldCheck,
  Search,
  Download,
  AlertTriangle,
  PlayCircle,
  Eye,
  CheckSquare,
  ArrowRight
} from 'lucide-react';
import { API_URL } from '../../../utils/constant';
import Swal from 'sweetalert2';

const StockRemovePage = () => {
  const [selectedBrand, setSelectedBrand] = useState('RECK'); // 'RECK' | 'WSI'
  const [stockType, setStockType] = useState('all'); // 'all' | 'current_retail' | 'current_wholesale'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [executedResult, setExecutedResult] = useState(null);
  const [error, setError] = useState(null);

  // Filter & Search states
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'matched' | 'shortage' | 'unmatched' | 'sheets'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSheetFilter, setSelectedSheetFilter] = useState('ALL');

  const company_id = localStorage.getItem('company_id') || '4';

  const brandOptions = [
    {
      id: 'RECK',
      name: 'Dr. Reckeweg',
      title: 'Reckeweg Sale Stock',
      badge: '12 Sheets Supported',
      description: 'R-Series, Tonics, LATT, Biochemics, Biocombinations, MTs, Dilutions 6/30/200/1M/High Potency',
      icon: <Factory size={28} />,
      colorClass: 'bg-indigo text-white',
      borderClass: 'border-indigo'
    },
    {
      id: 'WSI',
      name: 'Schwabe (WSI)',
      title: 'WSI Sale Stock',
      badge: '19 Sheets Supported',
      description: 'MT 30/100ml, FM/NFM Dilutions, LATT, Specialities, Biochemics 25/550g, Biocombinations, Naturals',
      icon: <Package size={28} />,
      colorClass: 'bg-success text-white',
      borderClass: 'border-success'
    }
  ];

  const handleFileSelect = (file) => {
    if (!file) return;
    const extension = file.name.split('.').pop().toLowerCase();
    if (extension !== 'xlsx' && extension !== 'xls') {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File Format',
        text: 'Please upload an Excel file (.xlsx or .xls)'
      });
      return;
    }
    setUploadedFile(file);
    setError(null);
    setPreviewResult(null);
    setExecutedResult(null);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  // Step 1: Preview & Verify File (Simulation - Dry Run)
  const handleVerifyPreview = async () => {
    if (!uploadedFile) {
      Swal.fire({
        icon: 'warning',
        title: 'No File Selected',
        text: 'Please select or drop an Excel file to verify.'
      });
      return;
    }

    if (!selectedBrand) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Brand',
        text: 'Please select Dr. Reckeweg or Schwabe (WSI) before verifying.'
      });
      return;
    }

    setIsProcessing(true);
    setError(null);
    setExecutedResult(null);

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('brandType', selectedBrand);
    formData.append('stockType', stockType);
    formData.append('company_id', company_id);

    try {
      const response = await fetch(`${API_URL}/jnm-import/stock-remove/preview`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to verify file');
      }

      setPreviewResult(data);
      Swal.fire({
        icon: 'info',
        title: 'Verification Complete',
        html: `
          <div class="text-start">
            <p class="text-primary fw-bold mb-2">Simulation completed without modifying the database.</p>
            <p><b>Brand:</b> ${data.brand}</p>
            <p><b>Total Rows:</b> ${data.totalRowsProcessed}</p>
            <p><b>Matched:</b> <span class="text-success font-weight-bold">${data.totalMatched}</span> | <b>Unmatched:</b> <span class="text-danger font-weight-bold">${data.totalUnmatched}</span></p>
            <p><b>Total Deductible Qty:</b> <span class="text-success font-weight-bold">${data.totalDeducted} units</span></p>
            <p class="text-muted small">Please verify all rows below and click <b>"Confirm & Remove Stock"</b> when ready.</p>
          </div>
        `,
        confirmButtonText: 'Review Details',
        confirmButtonColor: '#0d6efd'
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: err.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2: Confirm & Execute Real Stock Removal from DB
  const handleExecuteRemoval = async () => {
    if (!uploadedFile) return;

    const brandObj = brandOptions.find(b => b.id === selectedBrand);
    const activeData = previewResult || {};

    const confirm = await Swal.fire({
      title: `Confirm Stock Removal?`,
      html: `
        <div class="text-start">
          <p class="text-danger fw-bold mb-2">This action will permanently deduct stock from the database!</p>
          <div class="p-3 bg-light rounded border mb-3">
            <p class="mb-1"><b>Brand:</b> <span class="badge bg-dark">${brandObj?.name}</span></p>
            <p class="mb-1"><b>File:</b> <code>${uploadedFile.name}</code></p>
            <p class="mb-1"><b>Quantity to Deduct:</b> <span class="text-danger fw-bold">${activeData.totalDeducted || 0} units</span></p>
            <p class="mb-0"><b>Non-Batch:</b> ${activeData.totalDeductedNoBatch || 0} | <b>Batch:</b> ${activeData.totalDeductedWithBatch || 0}</p>
          </div>
          <p class="text-muted small mb-0">Are you sure you want to apply these changes now?</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Deduct Stock from Database',
      cancelButtonText: 'Cancel & Review More',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (!confirm.isConfirmed) return;

    setIsExecuting(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('brandType', selectedBrand);
    formData.append('stockType', stockType);
    formData.append('company_id', company_id);

    try {
      const response = await fetch(`${API_URL}/jnm-import/stock-remove/execute`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to execute stock removal');
      }

      setExecutedResult(data);
      setPreviewResult(null);

      Swal.fire({
        icon: 'success',
        title: 'Stock Removed Successfully!',
        html: `
          <div class="text-start">
            <p class="text-success fw-bold mb-2">Live database records have been updated.</p>
            <p><b>Brand:</b> ${data.brand}</p>
            <p><b>Total Deducted:</b> <span class="text-success font-weight-bold">${data.totalDeducted} units</span></p>
            <p><b>Variations Updated:</b> ${data.appliedUpdatesCount || 0}</p>
            <p><b>From Non-Batch:</b> ${data.totalDeductedNoBatch} | <b>From Batch:</b> ${data.totalDeductedWithBatch}</p>
            ${data.totalShortage > 0 ? `<p><b>Shortage:</b> <span class="text-danger font-weight-bold">${data.totalShortage} units</span></p>` : ''}
          </div>
        `,
        confirmButtonColor: '#198754'
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
      Swal.fire({
        icon: 'error',
        title: 'Stock Removal Failed',
        text: err.message
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setPreviewResult(null);
    setExecutedResult(null);
    setError(null);
    setSearchQuery('');
    setSelectedSheetFilter('ALL');
  };

  // Active dataset (Preview or Executed)
  const currentResult = executedResult || previewResult;
  const isLive = !!executedResult;

  // Export Results to CSV
  const handleExportCSV = () => {
    if (!currentResult || !currentResult.itemsLog || currentResult.itemsLog.length === 0) return;

    const headers = ['Sheet Name', 'Medicine Name', 'MRP', 'Requested Qty', 'Match Status', 'Product in DB', 'Target Variant', 'Deducted No-Batch', 'Deducted Batch', 'Total Deducted', 'Shortage', 'Batches Used'];
    const rows = currentResult.itemsLog.map(item => [
      item.sheetName || '',
      `"${(item.medicineName || '').replace(/"/g, '""')}"`,
      item.mrp != null ? item.mrp : '',
      item.qty || 0,
      item.status || '',
      `"${(item.productName || '').replace(/"/g, '""')}"`,
      `"${(item.targetVariant || '').replace(/"/g, '""')}"`,
      item.deductedNoBatch || 0,
      item.deductedWithBatch || 0,
      item.deductedTotal || 0,
      item.shortage || 0,
      `"${(item.batchNumbers || []).join('; ')}"`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Stock_Removal_${isLive ? 'Live' : 'Verification'}_${currentResult.brand || 'Result'}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered Items for Display
  const filteredItems = (currentResult?.itemsLog || []).filter(item => {
    if (selectedSheetFilter !== 'ALL' && item.sheetName !== selectedSheetFilter) {
      return false;
    }
    if (activeTab === 'matched' && item.status === 'UNMATCHED') return false;
    if (activeTab === 'shortage' && !(item.shortage > 0)) return false;
    if (activeTab === 'unmatched' && item.status !== 'UNMATCHED') return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const medMatch = (item.medicineName || '').toLowerCase().includes(q);
      const prodMatch = (item.productName || '').toLowerCase().includes(q);
      const sheetMatch = (item.sheetName || '').toLowerCase().includes(q);
      if (!medMatch && !prodMatch && !sheetMatch) return false;
    }
    return true;
  });

  const sheetNamesList = currentResult?.sheets ? Object.keys(currentResult.sheets) : [];

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0 bg-white" style={{ borderRadius: '14px' }}>
            <div className="card-body p-4">
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-danger text-white px-3 py-2" style={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                      <TrendingDown size={16} className="me-1 d-inline" /> Stock Removal
                    </span>
                    <span className="badge bg-light text-dark border px-3 py-2" style={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                      2-Step Verification & Deduction
                    </span>
                  </div>
                  <h4 className="fw-bold text-dark mt-2 mb-1">Dr. Reckeweg & Schwabe (WSI) Stock Removal</h4>
                  <p className="text-muted mb-0">
                    Upload your sale Excel file to verify deductibility, review items and shortages, and confirm removal from stock.
                  </p>
                </div>
                {uploadedFile && (
                  <button onClick={handleReset} className="btn btn-outline-secondary btn-sm mt-3 mt-md-0 d-flex align-items-center gap-1">
                    <RefreshCw size={14} /> Start Over / Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: Selection & Verification Form */}
      <div className="row g-4 mb-4">
        {/* Brand Selection */}
        <div className="col-lg-6">
          <div className="card shadow-sm border-0 h-100 bg-white" style={{ borderRadius: '14px' }}>
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
              <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                <ShieldCheck size={20} className="text-primary" /> 1. Select Brand & Stock Target
              </h5>
              <p className="text-muted small mb-0">Choose whether this file is for Dr. Reckeweg or Schwabe (WSI).</p>
            </div>
            <div className="card-body p-4">
              <div className="row g-3">
                {brandOptions.map(b => {
                  const isSelected = selectedBrand === b.id;
                  return (
                    <div className="col-md-6" key={b.id}>
                      <div
                        onClick={() => {
                          setSelectedBrand(b.id);
                          setPreviewResult(null);
                          setExecutedResult(null);
                        }}
                        className={`p-3 h-100 rounded-3 border text-start cursor-pointer transition-all ${
                          isSelected ? 'border-primary shadow-sm' : 'border-light-subtle'
                        }`}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderColor: isSelected ? '#3b82f6' : '#e2e8f0',
                          backgroundColor: isSelected ? '#eff6ff' : '#ffffff'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className={`p-2 rounded-2 ${isSelected ? 'bg-primary text-white' : 'bg-light text-dark border'}`}>
                            {b.icon}
                          </div>
                          {isSelected && <CheckCircle size={20} className="text-primary" />}
                        </div>
                        <h6 className="fw-bold mb-1" style={{ color: '#0f172a' }}>{b.name}</h6>
                        <span className="badge bg-secondary text-white small mb-2" style={{ fontSize: '0.75rem' }}>
                          {b.badge}
                        </span>
                        <p className="text-muted small mb-0" style={{ fontSize: '0.8rem', lineHeight: '1.3' }}>
                          {b.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Target Stock Field Selector */}
              <div className="mt-4 pt-3 border-top">
                <label className="form-label fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                  <Store size={16} className="text-secondary" /> Deduct Stock From:
                </label>
                <div className="d-flex flex-wrap gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="stockTypeRadio"
                      id="stockAll"
                      checked={stockType === 'all'}
                      onChange={() => {
                        setStockType('all');
                        setPreviewResult(null);
                        setExecutedResult(null);
                      }}
                    />
                    <label className="form-check-label small fw-bold text-primary cursor-pointer" htmlFor="stockAll" style={{ cursor: 'pointer' }}>
                      Retail & Wholesale (Auto - Recommended)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="stockTypeRadio"
                      id="stockRetail"
                      checked={stockType === 'current_retail'}
                      onChange={() => {
                        setStockType('current_retail');
                        setPreviewResult(null);
                        setExecutedResult(null);
                      }}
                    />
                    <label className="form-check-label small fw-medium cursor-pointer" htmlFor="stockRetail" style={{ cursor: 'pointer' }}>
                      Retail Only (<code>qty</code>)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="stockTypeRadio"
                      id="stockWholesale"
                      checked={stockType === 'current_wholesale'}
                      onChange={() => {
                        setStockType('current_wholesale');
                        setPreviewResult(null);
                        setExecutedResult(null);
                      }}
                    />
                    <label className="form-check-label small fw-medium cursor-pointer" htmlFor="stockWholesale" style={{ cursor: 'pointer' }}>
                      Wholesale Only (<code>current_wholesale</code>)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload & Action */}
        <div className="col-lg-6">
          <div className="card shadow-sm border-0 h-100 bg-white" style={{ borderRadius: '14px' }}>
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
              <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                <Upload size={20} className="text-danger" /> 2. Upload Excel File & Verify
              </h5>
              <p className="text-muted small mb-0">Upload the sale file to run simulation and verify deductibility.</p>
            </div>
            <div className="card-body p-4 d-flex flex-column justify-content-between">
              {/* Dropzone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`p-4 border-2 border-dashed rounded-3 text-center transition-all ${
                  isDragging ? 'bg-light border-primary' : 'border-secondary-subtle bg-light'
                }`}
                style={{
                  cursor: 'pointer',
                  borderStyle: 'dashed',
                  borderColor: isDragging ? '#3b82f6' : '#cbd5e1'
                }}
                onClick={() => document.getElementById('fileInput').click()}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept=".xlsx, .xls"
                  className="d-none"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleFileSelect(e.target.files[0]);
                    }
                  }}
                />
                <div className="d-flex flex-column align-items-center">
                  <div className="p-3 bg-white rounded-circle shadow-sm mb-2 text-danger">
                    <FileSpreadsheet size={32} />
                  </div>
                  {uploadedFile ? (
                    <div>
                      <span className="badge bg-success mb-1">File Loaded</span>
                      <h6 className="fw-bold text-dark mb-0">{uploadedFile.name}</h6>
                      <span className="text-muted small">{(uploadedFile.size / 1024).toFixed(1)} KB</span>
                    </div>
                  ) : (
                    <div>
                      <h6 className="fw-bold text-dark mb-1">Click to Upload or Drag & Drop</h6>
                      <p className="text-muted small mb-0">Supports multi-sheet Reckeweg & WSI Excel files</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Deduction Logic Explanation Banner */}
              <div className="p-3 bg-warning-subtle border border-warning rounded-2 mt-3 text-start">
                <div className="d-flex gap-2">
                  <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-1" />
                  <div className="small">
                    <span className="fw-bold text-dark">Deduction Hierarchy:</span>
                    <ul className="mb-0 ps-3 mt-1 text-secondary">
                      <li><b>Exact MRP:</b> Prioritizes matching price variations first.</li>
                      <li><b>Priority 1:</b> Deducts from <b>No-Batch variations</b> (<code>batch_no IS NULL</code>).</li>
                      <li><b>Priority 2:</b> If no-batch stock is exhausted, deducts from <b>Batch variations</b>.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Verify / Preview Button */}
              <button
                onClick={handleVerifyPreview}
                disabled={!uploadedFile || isProcessing || isExecuting}
                className="btn btn-primary w-100 py-3 mt-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                style={{ borderRadius: '10px' }}
              >
                {isProcessing ? (
                  <>
                    <RefreshCw size={18} className="spinner-border spinner-border-sm" />
                    <span>Analyzing & Verifying File...</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} />
                    <span>Verify & Preview Stock Removal</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="alert alert-danger shadow-sm border-0 d-flex align-items-center gap-2 mb-4" style={{ borderRadius: '12px' }}>
          <AlertCircle size={20} />
          <div className="fw-medium">{error}</div>
        </div>
      )}

      {/* Step 2: Verification Results & Execution Confirmation Banner */}
      {currentResult && (
        <div className="row mb-4">
          <div className="col-12">
            {/* Live vs Preview Notification Banner */}
            <div
              className={`card shadow-sm border-0 mb-4 ${
                isLive ? 'bg-success text-white' : 'bg-primary text-white'
              }`}
              style={{ borderRadius: '14px' }}
            >
              <div className="card-body p-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-3 bg-white rounded-3 text-dark shadow-sm">
                      {isLive ? <CheckCircle size={32} className="text-success" /> : <ShieldCheck size={32} className="text-primary" />}
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className={`badge ${isLive ? 'bg-dark' : 'bg-warning text-dark'} px-2 py-1`}>
                          {isLive ? 'LIVE APPLIED' : 'SIMULATION / VERIFICATION MODE'}
                        </span>
                        <span className="badge bg-white text-dark px-2 py-1">
                          Brand: {currentResult.brand}
                        </span>
                      </div>
                      <h4 className="fw-bold mb-1">
                        {isLive ? 'Stock Successfully Removed from Database!' : 'Verification Complete — Ready to Remove Stock'}
                      </h4>
                      <p className="mb-0 text-white-50 small">
                        {isLive
                          ? `Database updated successfully for ${currentResult.totalDeducted} units across ${currentResult.appliedUpdatesCount || 0} variations.`
                          : `No changes have been made to the database yet. Review the analysis below and click "Confirm & Remove Stock" to proceed.`}
                      </p>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <button onClick={handleExportCSV} className="btn btn-light text-dark fw-bold d-flex align-items-center gap-1 shadow-sm">
                      <Download size={16} /> Export CSV
                    </button>
                    {!isLive && (
                      <button
                        onClick={handleExecuteRemoval}
                        disabled={isExecuting || currentResult.totalDeducted === 0}
                        className="btn btn-danger fw-bold d-flex align-items-center gap-2 shadow-sm px-4"
                        style={{ border: '2px solid white' }}
                      >
                        {isExecuting ? (
                          <>
                            <RefreshCw size={18} className="spinner-border spinner-border-sm" />
                            <span>Applying to Database...</span>
                          </>
                        ) : (
                          <>
                            <TrendingDown size={18} />
                            <span>Confirm & Remove Stock from Database</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Metric KPI Cards */}
            <div className="row g-3 mb-4">
              <div className="col-sm-6 col-lg-3">
                <div className="card shadow-sm border-0 bg-white h-100" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-muted small fw-bold">PROCESSED ROWS</span>
                    <h3 className="fw-bold text-dark mt-1 mb-0">{currentResult.totalRowsProcessed}</h3>
                    <div className="text-muted small mt-1">Across {currentResult.totalSheets} sheets</div>
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-lg-3">
                <div className="card shadow-sm border-0 bg-white h-100" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-success small fw-bold">MATCHED PRODUCTS</span>
                    <h3 className="fw-bold text-success mt-1 mb-0">
                      {currentResult.totalMatched}
                      <span className="text-muted small fw-normal ms-2" style={{ fontSize: '0.9rem' }}>
                        ({((currentResult.totalMatched / (currentResult.totalRowsProcessed || 1)) * 100).toFixed(1)}%)
                      </span>
                    </h3>
                    <div className="text-muted small mt-1">{currentResult.totalUnmatched} unmatched rows</div>
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-lg-3">
                <div className="card shadow-sm border-0 bg-white h-100" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-danger small fw-bold">TOTAL DEDUCTION</span>
                    <h3 className="fw-bold text-danger mt-1 mb-0">
                      {currentResult.totalDeducted} <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>units</span>
                    </h3>
                    <div className="text-muted small mt-1">
                      Non-Batch: <b>{currentResult.totalDeductedNoBatch}</b> | Batch: <b>{currentResult.totalDeductedWithBatch}</b>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-lg-3">
                <div className="card shadow-sm border-0 bg-white h-100" style={{ borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <span className="text-warning small fw-bold">SHORTAGES / DEFICIT</span>
                    <h3 className="fw-bold text-warning mt-1 mb-0">
                      {currentResult.totalShortage} <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>units</span>
                    </h3>
                    <div className="text-muted small mt-1">
                      {currentResult.shortageList?.length || 0} items with insufficient stock
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Table & Tabs */}
            <div className="card shadow-sm border-0 bg-white" style={{ borderRadius: '14px' }}>
              <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pb-3 border-bottom">
                  {/* Tabs */}
                  <ul className="nav nav-pills gap-2">
                    <li className="nav-item">
                      <button
                        onClick={() => setActiveTab('all')}
                        className={`nav-link btn-sm ${activeTab === 'all' ? 'active bg-primary' : 'bg-light text-dark'}`}
                      >
                        All Items ({currentResult.itemsLog?.length || 0})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        onClick={() => setActiveTab('matched')}
                        className={`nav-link btn-sm ${activeTab === 'matched' ? 'active bg-success' : 'bg-light text-dark'}`}
                      >
                        Matched ({currentResult.totalMatched})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        onClick={() => setActiveTab('shortage')}
                        className={`nav-link btn-sm ${activeTab === 'shortage' ? 'active bg-warning text-dark' : 'bg-light text-dark'}`}
                      >
                        Shortages ({currentResult.shortageList?.length || 0})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        onClick={() => setActiveTab('unmatched')}
                        className={`nav-link btn-sm ${activeTab === 'unmatched' ? 'active bg-danger' : 'bg-light text-dark'}`}
                      >
                        Unmatched ({currentResult.unmatchedList?.length || 0})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        onClick={() => setActiveTab('sheets')}
                        className={`nav-link btn-sm ${activeTab === 'sheets' ? 'active bg-dark' : 'bg-light text-dark'}`}
                      >
                        Sheets Breakdown ({sheetNamesList.length})
                      </button>
                    </li>
                  </ul>

                  {/* Filters */}
                  {activeTab !== 'sheets' && (
                    <div className="d-flex flex-wrap gap-2">
                      {sheetNamesList.length > 0 && (
                        <select
                          className="form-select form-select-sm"
                          style={{ width: '180px' }}
                          value={selectedSheetFilter}
                          onChange={(e) => setSelectedSheetFilter(e.target.value)}
                        >
                          <option value="ALL">All Sheets</option>
                          {sheetNamesList.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      )}
                      <div className="input-group input-group-sm" style={{ width: '220px' }}>
                        <span className="input-group-text bg-light border-end-0">
                          <Search size={14} className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search medicine..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card-body p-0">
                {activeTab === 'sheets' ? (
                  /* Sheet Summary Breakdown */
                  <div className="table-responsive p-3">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Sheet Name</th>
                          <th>Total Rows</th>
                          <th>Matched</th>
                          <th>Unmatched</th>
                          <th>Requested Qty</th>
                          <th>Deducted (No-Batch)</th>
                          <th>Deducted (Batch)</th>
                          <th>Total Deducted</th>
                          <th>Shortage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sheetNamesList.map(sName => {
                          const s = currentResult.sheets[sName];
                          return (
                            <tr key={sName}>
                              <td className="fw-bold">{s.sheetName}</td>
                              <td>{s.totalRows}</td>
                              <td className="text-success fw-bold">{s.matchedRows}</td>
                              <td className={s.unmatchedRows > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                                {s.unmatchedRows}
                              </td>
                              <td className="fw-bold">{s.qtyRequested}</td>
                              <td className="text-primary">{s.qtyDeductedNoBatch}</td>
                              <td className="text-warning">{s.qtyDeductedWithBatch}</td>
                              <td className="text-success fw-bold">{s.qtyDeductedTotal}</td>
                              <td className={s.shortageTotal > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                                {s.shortageTotal}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Items Table */
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Sheet</th>
                          <th>Medicine Name</th>
                          <th>MRP</th>
                          <th>Req Qty</th>
                          <th>Matched Product in DB</th>
                          <th>Target Variant</th>
                          <th>Status</th>
                          <th>Deducted (No-Batch)</th>
                          <th>Deducted (Batch)</th>
                          <th>Total Deducted</th>
                          <th>Shortage</th>
                          <th>Batches Used</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.length === 0 ? (
                          <tr>
                            <td colSpan="12" className="text-center py-4 text-muted">
                              No items match the current filter criteria.
                            </td>
                          </tr>
                        ) : (
                          filteredItems.map((item, idx) => (
                            <tr key={idx}>
                              <td>
                                <span className="badge bg-light text-dark border">{item.sheetName}</span>
                              </td>
                              <td className="fw-bold">{item.medicineName}</td>
                              <td>{item.mrp != null ? `₹${item.mrp}` : '-'}</td>
                              <td className="fw-bold">{item.qty}</td>
                              <td>
                                {item.productName ? (
                                  <span className="text-dark fw-medium">{item.productName}</span>
                                ) : (
                                  <span className="text-danger small">Not Found</span>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-secondary-subtle text-secondary border">
                                  {item.targetVariant || '-'}
                                </span>
                              </td>
                              <td>
                                {item.status === 'MATCHED' && (
                                  <span className="badge bg-success-subtle text-success border border-success-subtle">
                                    Fully Matched
                                  </span>
                                )}
                                {item.status === 'PARTIAL_SHORTAGE' && (
                                  <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                                    Partial Stock ({item.deductedTotal}/{item.qty})
                                  </span>
                                )}
                                {item.status === 'FULL_SHORTAGE' && (
                                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                                    0 Stock Available
                                  </span>
                                )}
                                {item.status === 'UNMATCHED' && (
                                  <span className="badge bg-danger text-white">
                                    Unmatched Product
                                  </span>
                                )}
                              </td>
                              <td className="text-primary fw-medium">{item.deductedNoBatch || 0}</td>
                              <td className="text-warning fw-medium">{item.deductedWithBatch || 0}</td>
                              <td className="text-success fw-bold">{item.deductedTotal || 0}</td>
                              <td className={item.shortage > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                                {item.shortage || 0}
                              </td>
                              <td>
                                {(item.batchNumbers || []).length > 0 ? (
                                  <span className="badge bg-light text-dark border">
                                    {item.batchNumbers.join(', ')}
                                  </span>
                                ) : (
                                  <span className="text-muted small">None (Non-Batch)</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockRemovePage;
